import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gauge, Search, ChevronDown, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { platforms, comparisonPairs, globalFaqs } from "../data/mock";

const sortedByRank = [...platforms].sort((a, b) => a.rank - b.rank);
const comparisonPath = (comparison) => comparison.path || `/compare?platforms=${comparison.slugs.join(",")}`;

const navItems = [
  { label: "Home", to: "/" },
  {
    label: "E-Commerce Platforms",
    children: [
      { label: "Best Ecommerce Builders", to: "/best-ecommerce-builders" },
      { label: "Best AI Ecommerce Platforms", to: "/best-ai-ecommerce-platforms" },
      { label: "Best Mobile Ecommerce Platforms", to: "/best-mobile-ecommerce-platforms" },
      { label: "Best for Small Business", to: "/best-for-small-business" },
    ],
  },
  {
    label: "Comparisons",
    children: [
      ...comparisonPairs.filter((comparison) => comparison.path).map((comparison) => ({ label: comparison.label, to: comparisonPath(comparison) })),
      { label: "View All Comparisons →", to: "/ecommerce-comparisons" },
    ],
  },
  {
    label: "Reviews",
    children: sortedByRank.slice(0, 6).map((p) => ({ label: `${p.name} Review`, to: `/${p.slug}-review` })),
  },
  { label: "Methodology", to: "/methodology" },
  {
    label: "Resources",
    children: [
      { label: "About Us", to: "/about" },
      { label: "Trust Center", to: "/trust-center" },
      { label: "Editorial Policy", to: "/editorial-policy" },
      { label: "Disclosure", to: "/disclosure" },
      { label: "Contact Editorial", to: "/contact" },
    ],
  },
];

const NavDropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button data-testid={`header-menu-${item.label.toLowerCase().replaceAll(" ", "-")}`} className="flex items-center gap-1 text-sm font-medium text-slate-100 hover:text-white transition-colors py-2">
        {item.label}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-0 w-64 bg-white rounded-lg shadow-xl border border-slate-100 py-2 z-50">
          {item.children.map((c) => {
            const label = typeof c === "string" ? c : c.label;
            const to = typeof c === "string" ? null : c.to;
            if (to && to.startsWith("mailto:")) {
              return (
                <a key={label} data-testid={`header-link-${label.toLowerCase().replaceAll(" ", "-").replaceAll("→", "all")}`} href={to} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors">
                  {label}
                </a>
              );
            }
            if (to) {
              return (
                <Link key={label} data-testid={`header-link-${label.toLowerCase().replaceAll(" ", "-").replaceAll("→", "all")}`} to={to} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors">
                  {label}
                </Link>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

const SearchBox = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const q = query.trim().toLowerCase();

  const platformResults = q
    ? platforms.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.tagline.toLowerCase().includes(q) ||
          p.bestFor.some((b) => b.replace("-", " ").includes(q))
      )
    : [];

  const comparisonResults = q
    ? comparisonPairs.filter((c) => c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q))
    : [];

  const faqResults = q
    ? globalFaqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
    : [];

  const totalResults = platformResults.length + comparisonResults.length + faqResults.length;

  const goTo = (slug) => {
    navigate(`/${slug}-review`);
    setOpen(false);
    setQuery("");
  };

  const goToCompare = (slugs) => {
    navigate(`/compare?platforms=${slugs.join(",")}`);
    setOpen(false);
    setQuery("");
  };

  const goToFaq = () => {
    navigate("/#faq");
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        data-testid="header-search-button"
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-full flex items-center justify-center text-slate-200 hover:bg-white/10 transition-colors"
        aria-label="Search"
      >
        <Search className="w-4.5 h-4.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-100 p-3 z-50">
          <Input
            data-testid="header-search-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search platforms, comparisons, FAQs..."
            className="mb-2"
          />
          {q && (
            <div className="max-h-80 overflow-y-auto">
              {totalResults === 0 ? (
                <p className="text-sm text-slate-400 px-2 py-3">No results match "{query}".</p>
              ) : (
                <>
                  {platformResults.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase px-2 py-1">Platforms</p>
                      {platformResults.map((p) => (
                        <button
                          data-testid={`search-platform-result-${p.slug}`}
                          key={p.slug}
                          onClick={() => goTo(p.slug)}
                          className="w-full flex items-center justify-between text-left px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div>
                            <p className="text-sm font-semibold text-[#111827]">{p.name}</p>
                            <p className="text-xs text-slate-500">{p.tagline}</p>
                          </div>
                          <span className="text-xs font-bold text-indigo-600">{p.fitRating}/10 fit</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {comparisonResults.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase px-2 py-1">Comparisons</p>
                      {comparisonResults.map((c) => (
                        <button
                          data-testid={`search-comparison-result-${c.slugs.join("-")}`}
                          key={c.label}
                          onClick={() => goToCompare(c.slugs)}
                          className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <p className="text-sm font-semibold text-[#111827]">{c.label}</p>
                          <p className="text-xs text-slate-500">{c.description}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {faqResults.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase px-2 py-1">FAQs</p>
                      {faqResults.map((f) => (
                        <button
                          data-testid={`search-faq-result-${f.q.toLowerCase().replaceAll(" ", "-").slice(0, 32)}`}
                          key={f.q}
                          onClick={goToFaq}
                          className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <p className="text-sm font-semibold text-[#111827]">{f.q}</p>
                          <p className="text-xs text-slate-500 line-clamp-1">{f.a}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-[#111827] border-b border-white/10">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-home-link" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-indigo-500 flex items-center justify-center">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-lg tracking-tight">E-Com Benchmark Lab</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) =>
            item.children ? (
              <NavDropdown key={item.label} item={item} />
            ) : (
              <Link
                key={item.label}
                data-testid={`header-link-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                to={item.to}
                className="text-sm font-medium text-slate-100 hover:text-white transition-colors"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <SearchBox />
          <Button
            data-testid="header-trust-center-button"
            onClick={() => navigate("/trust-center")}
            variant="outline"
            className="bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white rounded-full text-sm h-9"
          >
            Trust Center
          </Button>
        </div>

        <button data-testid="header-mobile-menu-button" aria-label="Toggle navigation" aria-expanded={mobileOpen} className="lg:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#111827] border-t border-white/10 px-4 pb-4">
          {navItems.map((item) => item.to ? (
            <Link key={item.label} data-testid={`mobile-nav-${item.label.toLowerCase().replaceAll(" ", "-")}`} to={item.to} className="block border-b border-white/5 py-3 text-sm font-medium text-white" onClick={() => setMobileOpen(false)}>{item.label}</Link>
          ) : (
            <details key={item.label} className="border-b border-white/5 py-2" data-testid={`mobile-nav-group-${item.label.toLowerCase().replaceAll(" ", "-")}`}>
              <summary className="cursor-pointer py-1 text-sm font-medium text-white">{item.label}</summary>
              <div className="mt-2 space-y-1 border-l border-white/15 pl-3">
                {item.children.map((child) => (
                  <Link key={child.label} data-testid={`mobile-nav-${child.label.toLowerCase().replaceAll(" ", "-").replaceAll("→", "all")}`} to={child.to} className="block py-2 text-sm text-slate-300" onClick={() => setMobileOpen(false)}>{child.label}</Link>
                ))}
              </div>
            </details>
          ))}
          <Button data-testid="mobile-trust-center-button" className="mt-3 w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => { navigate("/trust-center"); setMobileOpen(false); }}>
            Trust Center
          </Button>
        </div>
      )}
    </header>
  );
};

export default Header;
